import { useMemo, useState } from 'react';
import {
  Checkbox,
  Divider,
  Stack,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { twMerge } from 'tailwind-merge';

import { Unit, Process, ActionItem } from '@urbanmix-tech/shared-js';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';

export type ProcessHighlightProps = {
  units: Unit[];
  processes: Process[];
  actionItems: ActionItem[];
  processStatusFilter: string[];
  processTypeFilter: string[];
  unitProcessFilter: string[];
  onChangeProcessStatusFilter(selected: string[]): void;
  onChangeProcessTypeFilter(selected: string[]): void;
  onChangeUnitProcessFilter(selected: string[]): void;
};

export function ProcessHighlight({
  units,
  processes,
  actionItems,
  processStatusFilter,
  processTypeFilter,
  unitProcessFilter,
  onChangeProcessStatusFilter,
  onChangeProcessTypeFilter,
  onChangeUnitProcessFilter,
}: ProcessHighlightProps) {
  const [expanded, setExpanded] = useState(true);
  const { getProcessTypeConfig } = useProcessSettings();

  // Calculate process statistics
  const processStats = useMemo(() => {
    const activeProcesses = processes.filter((p) => p.status === 'start').length;
    const completedProcesses = processes.filter(
      (p) => p.status === 'completed'
    ).length;
    const cancelledProcesses = processes.filter(
      (p) => p.status === 'cancelled'
    ).length;
    const pendingProcesses = processes.filter(
      (p) => p.status !== 'start' && p.status !== 'completed' && p.status !== 'cancelled'
    ).length;

    return {
      start: activeProcesses,
      completed: completedProcesses,
      cancelled: cancelledProcesses,
      pending: pendingProcesses,
      total: processes.length,
    };
  }, [processes]);

  // Calculate process type breakdown
  const processTypeStats = useMemo(() => {
    const typeMap = new Map<string, { active: number; total: number }>();

    processes.forEach((process) => {
      const type = process.type || process.name || 'Other';
      const existing = typeMap.get(type) || { active: 0, total: 0 };
      typeMap.set(type, {
        active: existing.active + (process.status === 'start' ? 1 : 0),
        total: existing.total + 1,
      });
    });

    return Array.from(typeMap.entries())
      .map(([type, stats]) => ({
        type,
        ...stats,
      }))
      .sort((a, b) => b.total - a.total);
  }, [processes]);

  // Calculate unit and task statistics
  const unitTaskStats = useMemo(() => {
    const unitsInActiveProcesses = new Set(
      processes.filter((p) => p.status === 'start').map((p) => p.unitId)
    );

    const unitsWithoutProcesses = units.filter(
      (unit) => !processes.some((p) => p.unitId === unit.id)
    ).length;

    const activeTasks = actionItems.filter(
      (item) => item.status !== 'done'
    ).length;

    const now = new Date().getTime();
    const overdueTasks = actionItems.filter((item) => {
      if (item.status === 'done') return false;
      const dueDate = convertPseudoDateToRealDate(item.dueDate);
      return dueDate && dueDate.getTime() < now;
    }).length;

    return {
      unitsInProcess: unitsInActiveProcesses.size,
      idleUnits: unitsWithoutProcesses,
      activeTasks,
      overdueTasks,
    };
  }, [units, processes, actionItems]);

  const statusOptions = [
    { label: 'Active', value: 'start', count: processStats.start },
    {
      label: 'Pending',
      value: 'pending',
      count: processStats.pending,
    },
    {
      label: 'Completed',
      value: 'completed',
      count: processStats.completed,
    },
    {
      label: 'Cancelled',
      value: 'cancelled',
      count: processStats.cancelled,
    },
  ];

  const unitOptions = [
    { label: 'In Process', value: 'in-process', count: unitTaskStats.unitsInProcess },
    { label: 'Idle', value: 'idle', count: unitTaskStats.idleUnits },
  ];

  const handleRemoveFilter = (type: 'status' | 'processType' | 'unit', value: string) => {
    if (type === 'status') {
      onChangeProcessStatusFilter(processStatusFilter.filter((v) => v !== value));
    } else if (type === 'processType') {
      onChangeProcessTypeFilter(processTypeFilter.filter((v) => v !== value));
    } else {
      onChangeUnitProcessFilter(unitProcessFilter.filter((v) => v !== value));
    }
  };

  const hasActiveFilters =
    processStatusFilter.length > 0 ||
    processTypeFilter.length > 0 ||
    unitProcessFilter.length > 0;

  const handleClearAll = () => {
    onChangeProcessStatusFilter([]);
    onChangeProcessTypeFilter([]);
    onChangeUnitProcessFilter([]);
  };

  const getStatusLabel = (value: string) =>
    statusOptions.find((o) => o.value === value)?.label || value;
  const getUnitLabel = (value: string) =>
    unitOptions.find((o) => o.value === value)?.label || value;

  return (
    <Stack gap="8px">
      {/* Active Filters Chips */}
      {hasActiveFilters && (
        <Stack direction="row" gap="8px" alignItems="center" flexWrap="wrap">
          <Typography variant="caption" className="text-stone-300">
            Active Filters:
          </Typography>
          {processStatusFilter.map((value) => (
            <Chip
              key={value}
              label={getStatusLabel(value)}
              onDelete={() => handleRemoveFilter('status', value)}
              size="small"
              color="primary"
              sx={{
                height: '24px',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  '&:hover': {
                    color: 'white',
                  },
                },
              }}
            />
          ))}
          {processTypeFilter.map((value) => {
            const config = getProcessTypeConfig(value);
            return (
              <Chip
                key={value}
                label={config.displayName}
                onDelete={() => handleRemoveFilter('processType', value)}
                size="small"
                color="primary"
                sx={{
                  height: '24px',
                  backgroundColor: config.color,
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    '&:hover': {
                      color: 'white',
                    },
                  },
                }}
              />
            );
          })}
          {unitProcessFilter.map((value) => (
            <Chip
              key={value}
              label={getUnitLabel(value)}
              onDelete={() => handleRemoveFilter('unit', value)}
              size="small"
              color="primary"
              sx={{
                height: '24px',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '16px',
                  '&:hover': {
                    color: 'white',
                  },
                },
              }}
            />
          ))}
          <Chip
            label="Clear All"
            onClick={handleClearAll}
            size="small"
            variant="outlined"
            sx={{
              height: '24px',
              color: 'rgba(100, 116, 139, 1)',
              borderColor: 'rgba(100, 116, 139, 0.5)',
              '&:hover': {
                borderColor: 'rgba(100, 116, 139, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          />
        </Stack>
      )}

      {/* Collapsible Stats Section */}
      <Stack
        className="rounded-lg border border-stone-200/20 bg-zinc-900/30"
        sx={{ overflow: 'hidden' }}
      >
        {/* Collapse Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className="cursor-pointer px-12 py-8"
          onClick={() => setExpanded(!expanded)}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            },
          }}
        >
          <Typography variant="body2" className="text-stone-300">
            {expanded ? 'Hide' : 'Show'} Process Stats
          </Typography>
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
              color: 'rgba(255, 255, 255, 0.5)',
              padding: '4px',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Stats Panels */}
        <Collapse in={expanded}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 p-12">
            {/* Panel 1: Process Status Overview */}
            <div className="rounded-lg border border-white/5 bg-zinc-900 p-16 flex flex-col gap-12">
              <Typography variant="subtitle1" className="text-stone-200 font-semibold border-b border-white/5 pb-8">
                Process Status
              </Typography>

              <div className="flex flex-col gap-8">
                {statusOptions.map((status) => (
                  <div
                    key={status.value}
                    className="flex items-center justify-between group hover:bg-white/5 p-4 rounded transition-colors"
                  >
                    <Typography variant="body2" className="text-stone-300 group-hover:text-white">
                      {status.label}
                    </Typography>
                    <div className="flex items-center gap-8">
                      <div className="bg-zinc-800 px-6 py-2 rounded text-xs text-stone-400 font-medium">
                        {status.count}
                      </div>
                      <Checkbox
                        checked={processStatusFilter.includes(status.value)}
                        onChange={(_e, checked) => {
                          if (checked) {
                            onChangeProcessStatusFilter([
                              ...processStatusFilter.filter(
                                (item) => item !== status.value
                              ),
                              status.value,
                            ]);
                          } else {
                            onChangeProcessStatusFilter([
                              ...processStatusFilter.filter(
                                (item) => item !== status.value
                              ),
                            ]);
                          }
                        }}
                        size="small"
                        className="p-0"
                        sx={{ 
                          color: 'rgba(255,255,255,0.3)',
                          '&.Mui-checked': { color: '#3b82f6' } 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Panel 2: Process Type Breakdown */}
            <div className="rounded-lg border border-white/5 bg-zinc-900 p-16 flex flex-col gap-12">
              <Typography variant="subtitle1" className="text-stone-200 font-semibold border-b border-white/5 pb-8">
                Process Types
              </Typography>

              {processTypeStats.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <Typography variant="body2" className="text-stone-500 italic">
                    No active processes
                  </Typography>
                </div>
              ) : (
                <div className="flex flex-col gap-8">
                  {processTypeStats.slice(0, 5).map((typeData) => {
                    const config = getProcessTypeConfig(typeData.type);
                    return (
                      <div
                        key={typeData.type}
                        className="flex items-center justify-between group hover:bg-white/5 p-4 rounded transition-colors"
                      >
                        <div className="flex items-center gap-8 flex-1 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex-shrink-0"
                            style={{ backgroundColor: config.color }}
                          />
                          <Typography
                            variant="body2"
                            className="text-stone-300 truncate group-hover:text-white"
                            title={config.displayName}
                          >
                            {config.displayName}
                          </Typography>
                        </div>
                        <div className="flex items-center gap-8">
                          <div className="bg-zinc-800 px-6 py-2 rounded text-xs text-stone-400 font-medium whitespace-nowrap">
                            {typeData.active} / {typeData.total}
                          </div>
                          <Checkbox
                            checked={processTypeFilter.includes(typeData.type)}
                            onChange={(_e, checked) => {
                              if (checked) {
                                onChangeProcessTypeFilter([
                                  ...processTypeFilter.filter(
                                    (item) => item !== typeData.type
                                  ),
                                  typeData.type,
                                ]);
                              } else {
                                onChangeProcessTypeFilter([
                                  ...processTypeFilter.filter(
                                    (item) => item !== typeData.type
                                  ),
                                ]);
                              }
                            }}
                            size="small"
                            className="p-0"
                            sx={{ 
                              color: 'rgba(255,255,255,0.3)',
                              '&.Mui-checked': { color: '#3b82f6' } 
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {processTypeStats.length > 5 && (
                    <Typography variant="caption" className="text-stone-500 text-center pt-4">
                      +{processTypeStats.length - 5} more types
                    </Typography>
                  )}
                </div>
              )}
            </div>

            {/* Panel 3: Unit Coverage & Task Activity */}
            <div className="rounded-lg border border-white/5 bg-zinc-900 p-16 flex flex-col gap-12">
              <Typography variant="subtitle1" className="text-stone-200 font-semibold border-b border-white/5 pb-8">
                Activity Overview
              </Typography>

              <div className="flex flex-col gap-8 h-full">
                <div className="flex flex-col gap-8">
                  {unitOptions.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center justify-between group hover:bg-white/5 p-4 rounded transition-colors"
                    >
                      <Typography variant="body2" className="text-stone-300 group-hover:text-white">
                        {option.label}
                      </Typography>
                      <div className="flex items-center gap-8">
                        <div className="bg-zinc-800 px-6 py-2 rounded text-xs text-stone-400 font-medium">
                          {option.count} units
                        </div>
                        <Checkbox
                          checked={unitProcessFilter.includes(option.value)}
                          onChange={(_e, checked) => {
                            if (checked) {
                              onChangeUnitProcessFilter([
                                ...unitProcessFilter.filter(
                                  (item) => item !== option.value
                                ),
                                option.value,
                              ]);
                            } else {
                              onChangeUnitProcessFilter([
                                ...unitProcessFilter.filter(
                                  (item) => item !== option.value
                                ),
                              ]);
                            }
                          }}
                          size="small"
                          className="p-0"
                          sx={{ 
                            color: 'rgba(255,255,255,0.3)',
                            '&.Mui-checked': { color: '#3b82f6' } 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-16 border-t border-white/5 flex flex-col gap-8">
                  <div className="flex items-center justify-between p-4">
                    <Typography variant="body2" className="text-stone-300">
                      Active Tasks
                    </Typography>
                    <Typography variant="subtitle2" className="text-white font-semibold">
                      {unitTaskStats.activeTasks}
                    </Typography>
                  </div>

                  <div className="flex items-center justify-between p-4">
                    <Typography variant="body2" className="text-stone-300">
                      Overdue Tasks
                    </Typography>
                    <div className={twMerge(
                      "px-8 py-2 rounded font-semibold text-sm",
                      unitTaskStats.overdueTasks > 0 ? "bg-red-500/10 text-red-500" : "text-stone-500"
                    )}>
                      {unitTaskStats.overdueTasks}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </Stack>
    </Stack>
  );
}
